import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Brain, CircleNotch, ArrowClockwise, LinkedinLogo, Globe, UsersThree, MapPin } from "@phosphor-icons/react";
import { IntelDetails } from "@/components/OpportunityIntel";

const Fact = ({ icon: Icon, children, testid }) => (
  <span className="flex items-center gap-1.5 text-xs text-fg/80" data-testid={testid}><Icon size={13} className="text-accent" />{children}</span>
);

export const CompanyIntelCard = ({ companyId }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/research/companies/${companyId}/intel`).then(({ data }) => setD(data)).catch(() => setD({})), [companyId]);
  useEffect(() => { load(); }, [load]);
  const build = async (force) => {
    setBusy(true);
    try { const { data } = await api.post(`/research/companies/${companyId}/intel`, { force }); setD(data); toast.success("Fiche entreprise mise à jour"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!d || d.configured === false) return null;
  const p = d.profile || {};
  const has = d.research || d.profile;
  return (
    <section className="surface rounded-lg p-6 mb-6" data-testid="company-intel-card">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="overline flex items-center gap-2"><Brain size={14} className="text-accent" weight="fill" /> Fiche entreprise — recherche IA</div>
        <button onClick={() => build(!!has)} disabled={busy} data-testid="company-intel-build-btn"
          className="text-xs flex items-center gap-1.5 border border-accent/40 text-accent rounded-md px-3 py-1.5 hover:bg-accent/10 transition-colors disabled:opacity-60">
          {busy ? <CircleNotch size={12} className="animate-spin" /> : <ArrowClockwise size={12} />} {busy ? "Recherche en cours…" : has ? "Actualiser" : "Générer la fiche"}
        </button>
      </div>
      {!has ? (
        <p className="text-sm text-muted" data-testid="company-intel-empty">Projets IT, technologies, profils recherchés et recours à la prestation, à partir de sources web publiques citées.</p>
      ) : (
        <div className="space-y-4">
          {p.found && (
            <div className="flex flex-wrap gap-4" data-testid="company-intel-profile">
              {p.website && <Fact icon={Globe} testid="company-intel-website"><a href={p.website} target="_blank" rel="noreferrer" className="text-accent hover:underline">{p.domain || p.website}</a></Fact>}
              {p.linkedin_url && <Fact icon={LinkedinLogo}><a href={p.linkedin_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">LinkedIn</a></Fact>}
              {p.employees && <Fact icon={UsersThree}>~{p.employees} collaborateurs (estimation web)</Fact>}
              {p.hq_city && <Fact icon={MapPin}>Siège : {p.hq_city}{p.hq_country ? `, ${p.hq_country}` : ""}</Fact>}
            </div>
          )}
          {p.description && (
            <div data-testid="company-intel-description">
              <div className="text-[10px] uppercase tracking-wider text-muted/70 mb-1">Présentation publiée par l'entreprise (texte d'origine)</div>
              <p className="text-xs text-muted leading-relaxed">{p.description}</p>
            </div>
          )}
          {d.research && <IntelDetails intel={d.research} testid="company-intel-research" />}
          {d.research_at && <p className="text-[10px] text-muted">Mis à jour le {new Date(d.research_at).toLocaleString("fr-FR")} · informations publiques à vérifier avant tout contact.</p>}
        </div>
      )}
    </section>
  );
};
